import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import api from '../utils/api';

const SessionContext = createContext(null);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const heartbeatInterval = useRef(null);
  const currentCourseRef = useRef(null);
  const currentLessonRef = useRef(null);

  // Start session on mount
  const startSession = useCallback(async () => {
    try {
      const response = await api.post('/tracking/session/start', {
        user_agent: navigator.userAgent
      });
      setSessionToken(response.data.session_token);
      localStorage.setItem('session_token', response.data.session_token);
      return response.data.session_token;
    } catch (error) {
      console.error('Failed to start session:', error);
      return null;
    }
  }, []);

  // Send heartbeat
  const sendHeartbeat = useCallback(async () => {
    const token = sessionToken || localStorage.getItem('session_token');
    if (!token) return;

    try {
      const response = await api.post('/tracking/session/heartbeat', {
        session_token: token,
        course_id: currentCourseRef.current,
        lesson_id: currentLessonRef.current
      });
      if (response.data.duration) {
        setSessionDuration(response.data.duration);
      }
    } catch (error) {
      // Session might have expired, try to restart
      if (error.response?.status === 401) {
        const newToken = await startSession();
        if (newToken) {
          setSessionToken(newToken);
        }
      }
    }
  }, [sessionToken, startSession]);

  // End session
  const endSession = useCallback(async () => {
    const token = sessionToken || localStorage.getItem('session_token');
    if (!token) return;

    try {
      await api.post('/tracking/session/end', {
        session_token: token
      });
      localStorage.removeItem('session_token');
      setSessionToken(null);
    } catch (error) {
      console.error('Failed to end session:', error);
    }
  }, [sessionToken]);

  // Track course/lesson viewing
  const trackCourseView = useCallback((courseId, lessonId = null) => {
    currentCourseRef.current = courseId;
    currentLessonRef.current = lessonId;
  }, []);

  // Clear course tracking
  const clearCourseTracking = useCallback(() => {
    currentCourseRef.current = null;
    currentLessonRef.current = null;
  }, []);

  // Initialize session on component mount
  useEffect(() => {
    const token = localStorage.getItem('token'); // Auth token
    if (token) {
      startSession();
    }
  }, [startSession]);

  // Setup heartbeat interval
  useEffect(() => {
    if (sessionToken) {
      // Send heartbeat every 60 seconds
      heartbeatInterval.current = setInterval(sendHeartbeat, 60000);
      
      // Initial heartbeat
      sendHeartbeat();
    }

    return () => {
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
      }
    };
  }, [sessionToken, sendHeartbeat]);

  // Cleanup on unmount or page close
  useEffect(() => {
    const handleBeforeUnload = () => {
      endSession();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      endSession();
    };
  }, [endSession]);

  const value = {
    sessionToken,
    sessionDuration,
    startSession,
    endSession,
    trackCourseView,
    clearCourseTracking,
    formatDuration: (seconds) => {
      if (!seconds) return '0min';
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      if (hours > 0) {
        return `${hours}h ${minutes}min`;
      }
      return `${minutes}min`;
    }
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export default SessionContext;
